import React from 'react';
import { Award, Users, Wrench, Clock } from 'lucide-react';
import { businessInfo } from '../mock';
import { useTranslation, Trans } from 'react-i18next';

export const About = () => {
  const { t } = useTranslation();
  const stats = [
    { icon: Users, value: '500+', label: t('about_stats.customers') },
    { icon: Wrench, value: '15+', label: t('about_stats.experience') },
    { icon: Award, value: '100%', label: t('about_stats.satisfaction') },
    { icon: Clock, value: '24/48h', label: t('about_stats.turnaround') }
  ];

  const aboutList = t('about_list', { returnObjects: true });

  return (
    <section id="about" className="py-24 bg-neutral-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-lime-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-lime-600/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">
           <Trans i18nKey="about_title">
              Why Choose <span className="text-lime-400">K&A Motors Tech Solutions</span>
            </Trans>
          </h2>
          <p className="text-xl text-neutral-300 max-w-2xl mx-auto">
            { t('about_subtitle') }
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left: Text Content */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-3xl font-bold text-white">
                {t('about_h3')}
              </h3>
              <p className="text-neutral-300 text-lg leading-relaxed">
                {t('about_p1')}
              </p>
            </div>

            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-lime-400">{t('about_h4')}</h4>
              <ul className="space-y-3">
                {aboutList.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-lime-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <div className="w-2 h-2 bg-lime-400 rounded-full"></div>
                    </div>
                    <span className="text-neutral-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: Stats */}
          <div className="grid grid-cols-2 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="bg-neutral-800 border border-neutral-700 rounded-2xl p-6 text-center hover:border-lime-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-lime-500/10"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-lime-500/10 rounded-lg mb-4">
                    <Icon className="h-7 w-7 text-lime-400" />
                  </div>
                  <p className="text-4xl font-extrabold text-white mb-2">{stat.value}</p>
                  <p className="text-neutral-300 font-medium">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA Box */}
        <div className="bg-lime-500/10 border border-lime-500/30 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            {t('about_cta_h3')}
          </h3>
          <p className="text-neutral-300 text-lg mb-6 max-w-2xl mx-auto">
            {t('about_cta_p')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
               href={`https://wa.me/${businessInfo.whatsapp.replace(/\D/g, '')}`}
               target="_blank"
               rel="noopener noreferrer"
              className="inline-flex flex-col items-center bg-lime-500 hover:bg-lime-600 text-neutral-900 font-bold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105"
            >
              <svg className="h-5 w-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform " fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              <span>{t('btn_whatsapp')}</span>
              
            </a>
            <button
              onClick={() => document.querySelector('[data-booking-trigger]')?.click()}
              className="inline-block bg-transparent border-2 border-lime-500 text-lime-400 hover:bg-lime-500 hover:text-neutral-900 font-bold px-8 py-4 rounded-lg transition-all duration-300"
            >
              {t('btn_book_online')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
